/**
 * Planner-validator helpers (§7.1): graph shape (cycles over blocks ∪ parent, depth), WIP, holds. The graph handlers
 * in decide/graph.ts call these; failures are `Invalid` with messages an agent can act on.
 */
import { itemOf } from "./state"
import { descendantsOf, isPlannable } from "./tree"
import type { Actor, Hold, ItemId, ProjectState, WorkRef } from "./types"

const key = (r: WorkRef): string => `${r._tag}:${r.id}`

/**
 * Dependency graph over items: an edge u → v means "v waits for u". `blocks` edges go from → to and, because
 * readiness gates an item on `blocks` edges into its ancestors, also from → every descendant of `to`. A parent link
 * goes child → parent (a parent completes after its children). A cycle can never finish.
 */
const successors = (s: ProjectState) => {
  const out = new Map<string, WorkRef[]>()
  const add = (a: WorkRef, b: WorkRef) => {
    const k = key(a)
    const list = out.get(k) ?? []
    list.push(b)
    out.set(k, list)
  }
  for (const e of s.edges)
    if (e.kind === "blocks") {
      add(e.from, e.to)
      for (const d of descendantsOf(s, e.to.id)) add(e.from, { _tag: "item", id: d.id })
    }
  for (const t of s.items)
    if (t.parentId !== null) add({ _tag: "item", id: t.id }, { _tag: "item", id: t.parentId })
  return out
}

/**
 * Would adding `from blocks to` create a cycle? The new edge makes `to` and all its descendants wait for `from`, so it
 * closes a cycle when `from` is one of them or when any of them already (transitively) gates `from`: an item blocking
 * its own ancestor would wait on itself. Returns the cycle path if so.
 */
export const blocksCycle = (s: ProjectState, from: WorkRef, to: WorkRef): ReadonlyArray<WorkRef> | null => {
  if (key(from) === key(to)) return [from]
  const succ = successors(s)
  const target = key(from)
  const seen = new Set<string>()
  const stack: Array<{ node: WorkRef; path: WorkRef[] }> = [
    to,
    ...descendantsOf(s, to.id).map((d) => ({ _tag: "item" as const, id: d.id })),
  ].map((n) => ({ node: n, path: [n] }))
  while (stack.length > 0) {
    const top = stack.pop()
    if (!top) break
    const { node, path } = top
    const k = key(node)
    if (k === target) return [from, ...path]
    if (seen.has(k)) continue
    seen.add(k)
    for (const n of succ.get(k) ?? []) stack.push({ node: n, path: [...path, n] })
  }
  return null
}

/**
 * Decision 0094: why a blocks edge would make a cycle, by refs (never internal ids), and what the tree already says:
 * a parent waits for its children, so a child never blocks its ancestor and an ancestor never blocks its descendant.
 */
export const cycleMessage = (
  s: ProjectState,
  from: ItemId,
  to: ItemId,
  cycle: ReadonlyArray<WorkRef>,
): string => {
  const ref = (id: string) => itemOf(s, id as ItemId)?.ref ?? "the new item"
  if (descendantsOf(s, to).some((d) => d.id === from))
    return `${ref(from)} is inside ${ref(to)}: a parent already waits for its children, so this edge is implied. Leave it out.`
  if (descendantsOf(s, from).some((d) => d.id === to))
    return `${ref(to)} is inside ${ref(from)}: a parent finishes after its children, so it can't also block them. Block ${ref(to)} on a sibling, or move it out of ${ref(from)}.`
  const path = cycle.map((r) => ref(r.id)).join(" → ")
  return `this blocks edge would create a cycle (${path}): remove one of those edges first (RemoveEdge {from, to, kind}).`
}

/** Would re-parenting `id` under `parentId` make a cycle (the new parent is the item itself or below it)? */
export const parentCycle = (s: ProjectState, id: ItemId, parentId: ItemId): boolean =>
  id === parentId || descendantsOf(s, id).some((d) => d.id === parentId)

export const workExists = (s: ProjectState, r: WorkRef): boolean => itemOf(s, r.id) !== undefined

/** Holds bind agents and workers only; people are never held back by them (§3.3). */
export const holdViolation = (actor: Actor, hold: Hold, kind: "edit" | "move"): string | null => {
  if (actor._tag === "user") return null
  if (hold._tag === "locked") return "the record is locked by a person: comment or propose instead"
  if (hold._tag === "pinned" && kind === "move")
    return "the record is pinned by a person: its rank, parent, owner and assignee are fixed"
  return null
}

/**
 * Rule of Two (AreaPowers): `outward` tools need externalEffects; with all three powers on, every external effect
 * must be a person-approved outward call, so `write` tools are not grantable there.
 */
export const powersViolation = (
  powers: {
    readonly untrustedInput: boolean
    readonly privateData: boolean
    readonly externalEffects: boolean
  },
  grant: { readonly write: ReadonlyArray<string>; readonly outward: ReadonlyArray<string> },
): string | null => {
  if (grant.outward.length > 0 && !powers.externalEffects)
    return "outward tools need the area's externalEffects power"
  if (powers.untrustedInput && powers.privateData && powers.externalEffects && grant.write.length > 0)
    return "with all three powers on, effects go through person-approved outward tools only: no write tools"
  return null
}

/** WIP (invariant 13): top-level items of plannable types in Now, in an area or the whole project. */
export const nowCount = (s: ProjectState, areaId?: string): number =>
  s.items.filter(
    (i) =>
      i.parentId === null &&
      i.status === "in_progress" &&
      isPlannable(i) &&
      (areaId === undefined || i.areaId === areaId),
  ).length

/** An area's (or the project's) WIP: Now items and the limit. */
export const wipOf = (
  s: ProjectState,
  areaId: string | null,
): { readonly now: number; readonly max: number } => {
  if (areaId === null) return { now: nowCount(s), max: s.project?.wip.maxNow ?? 3 }
  const area = s.areas.find((a) => a.id === areaId)
  return { now: nowCount(s, areaId), max: area?.wip.maxNow ?? 1 }
}

/**
 * Wave A §4.3: where this commit took Now past the usual count, in words ("iOS has 2 in Now (usual: 1)"): each area,
 * then the project, when Now grew past `wip.maxNow`. WIP is a warning, never a refusal; empty when Now did not grow.
 */
export const wipWarnings = (before: ProjectState, after: ProjectState): ReadonlyArray<string> => {
  const out: string[] = []
  for (const area of after.areas) {
    const { now, max } = wipOf(after, area.id)
    if (now > max && now > nowCount(before, area.id))
      out.push(`${area.name} has ${now} in Now (usual: ${max})`)
  }
  const { now, max } = wipOf(after, null)
  if (now > max && now > nowCount(before)) out.push(`The project has ${now} in Now (usual: ${max})`)
  return out
}

/** Open plannable top-level items (create_item's `maxActive`). */
export const openPlannableCount = (s: ProjectState): number =>
  s.items.filter(
    (i) => i.parentId === null && isPlannable(i) && i.status !== "done" && i.status !== "cancelled",
  ).length
